import { errorToast, toast } from "@/v2-ui/toast"
import cn from "@/v2-ui/utils/utils.cn"
import Editor from "@/v2-backoffice-client/components/editor"
import TranslationAttributesHint from "@/v2-backoffice-client/translation/attributes/TranslationAttributesHint"
import {
  getAttributesEditorModelPath,
  parseAttributes,
  toAttributesDocument,
} from "@/v2-backoffice-client/translation/attributes/translation.attributes.utils"
import { useTranslationUpdateTypeValueMutation } from "@/v2-backoffice-client/translation/attributes/translation.updateTypeValue.mutation"
import type { TranslationRow } from "@/v2-backoffice-client/translation/translation.types"

export type TranslationAttributesTabProps = {
  className?: string
  row: TranslationRow
  /** Every value of the row's type set, for the shape hint. */
  siblingRows: TranslationRow[]
  /** Full catalog refetch — the saved document is not patched into the cache. */
  onSaved(): void
}

/**
 * The non-text half of a data label: the extra columns its type table carries
 * beyond Name and Description, which the runtime seed reads.
 *
 * This is the surface that used to be a manual database edit. A value created
 * from the Backoffice got NULL attributes, and the seed then failed on it until
 * someone opened v2-features-db and filled the column in by hand — so a type
 * table with more columns than Name/Description could not be maintained here at
 * all.
 *
 * Unlike a feature's Configuration, this writes the AUTHORING catalog only: the
 * runtime has no common.TypeValues to mirror into, it has the seeded type table
 * the next publish rebuilds from this column.
 */
function TranslationAttributesTab(props: TranslationAttributesTabProps) {
  const { row, siblingRows, onSaved, className } = props

  const [save, { loading: isSaving, error }] =
    useTranslationUpdateTypeValueMutation({
      onCompleted(data) {
        if (!data.updateTypeValue.Ok) {
          toast({
            variant: "destructive",
            title: "Failed to save attributes",
            description: data.updateTypeValue.Error ?? "unknown error",
          })
          return
        }
        toast({ title: `Saved the attributes of ${row.code}.` })
        onSaved()
      },
    })
  errorToast(error, { title: "Failed to save attributes" })

  return (
    <div className={cn("flex flex-col gap-3 p-3", className)}>
      <TranslationAttributesHint siblingRows={siblingRows} />
      <Editor
        className="min-h-[280px]"
        value={toAttributesDocument(row.attributes)}
        path={getAttributesEditorModelPath(row.categoryKey)}
        isSaving={isSaving}
        // The editor parses the draft itself and reports a syntax error inline;
        // what it cannot know is that this column must hold an OBJECT, since a
        // JSON array or scalar is valid JSON the seed can do nothing with.
        onSave={(document) => {
          const parsed = parseAttributes(document)
          if (!parsed.ok) {
            toast({
              variant: "destructive",
              title: "Attributes must be a JSON object",
              description: parsed.error,
            })
            return
          }
          void save({
            variables: {
              input: { ObjectKey: row.code, Attributes: parsed.attributes },
            },
          })
        }}
      />
    </div>
  )
}

export default TranslationAttributesTab
